import {useCallback, useState, useEffect} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import styled from 'styled-components';
import {Palette, CalendarProvider} from 'hubbl-shared';
import {
  GoogleButton,
  SecondaryButton,
  TextHeading3,
  TextBodyMedium,
  TextBodySmall,
  TextHeading6,
  WhiteContainer,
  ConfirmationModal,
} from '../index';
import {Api} from '../../api';
import {useI18n} from '../../hooks';
import {useAppDispatch} from '../../hooks/store/useAppDispatch';
import {fetchUserDetails} from '../../store/user/UserThunks';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 1rem;
`;

const ModalWrapper = styled(motion.div)`
  width: 480px;
  max-width: 100%;
  transform-origin: center;
  margin: auto;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const ModalInnerContent = styled.div`
  text-align: center;
  padding: 32px;

  @media (max-width: 480px) {
    padding: 24px;
  }
`;

const HeaderContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 48px;
`;

const BulletList = styled.ul`
  margin: 16px 0;
  padding-left: 0;
  list-style: none;
`;

const BulletItem = styled.li`
  padding: 8px 0;
  padding-left: 24px;
  position: relative;
  text-align: left;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${Palette.primaryPadua};
    font-weight: bold;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2rem;
  width: 100%;

  button {
    width: 100%;
  }
`;

const ErrorMessageContainer = styled(motion.div)`
  padding: 12px;
  background: #fee;
  border-radius: 8px;
  margin-top: 16px;
  text-align: center;
`;

const MessageContainer = styled(motion.div)`
  margin-top: 1rem;
  text-align: center;

  @media (max-width: 480px) {
    margin-top: 0.75rem;
  }
`;

const SuccessToastContainer = styled.div<{isVisible: boolean}>`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${Palette.primaryPadua};
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10003;
  display: ${props => (props.isVisible ? 'flex' : 'none')};
  align-items: center;
  gap: 8px;
`;

interface CalendarConnectionNudgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinueWithoutConnection: () => void;
  onSuccessfulConnection?: () => void;
  provider: CalendarProvider;
}

export const CalendarConnectionNudgeModal = ({
  isOpen,
  onClose,
  onContinueWithoutConnection,
  onSuccessfulConnection,
  provider,
}: CalendarConnectionNudgeModalProps) => {
  const t = useI18n();
  const dispatch = useAppDispatch();
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPopupBlockedModal, setShowPopupBlockedModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [authUrl, setAuthUrl] = useState<string>('');

  const providerName =
    provider === CalendarProvider.Google ? t.calendar.googleCalendar : provider;

  // Listen for OAuth success message from popup
  useEffect(() => {
    // Only listen if modal is actually open
    if (!isOpen) return;

    const handleMessage = async (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'CALENDAR_OAUTH_SUCCESS') {
        setIsConnecting(false);
        setShowSuccessToast(true);

        // Refresh user details for UI updates
        dispatch(fetchUserDetails());

        // Backend has safety checks to fetch fresh data, so we can proceed immediately
        setTimeout(() => {
          setShowSuccessToast(false);
          onClose();

          // Execute the original action (join event, show questions, etc.)
          if (onSuccessfulConnection) {
            onSuccessfulConnection();
          }
        }, 1500);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [dispatch, onClose, onSuccessfulConnection, isOpen]);

  const handleConnectCalendar = useCallback(async () => {
    setIsConnecting(true);
    setError(null);

    try {
      // Initiate OAuth flow
      const {authUrl, state} = await Api.calendar.initiateCalendarOAuth(
        provider,
        'web',
      );

      // Store state in sessionStorage for verification in callback
      sessionStorage.setItem('calendar_oauth_state', state);
      setAuthUrl(authUrl);

      // First attempt with popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'Calendar OAuth',
        `width=${width},height=${height},left=${left},top=${top},popup=1`,
      );

      // Check if popup was blocked
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        console.log('Popup blocked, showing user options');
        setShowPopupBlockedModal(true);
        setIsConnecting(false);
        return;
      }

      // Poll for popup closure
      const checkPopup = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopup);
            // The callback will handle the success/error
            // Close this modal after connection attempt
            setTimeout(() => {
              setIsConnecting(false);
              onClose();
            }, 1000);
          }
        } catch (e) {
          // Popup might be on different origin, ignore errors
        }
      }, 500);
    } catch (error) {
      console.error('Error initiating calendar OAuth:', error);
      setError(
        error instanceof Error
          ? error.message
          : t.calendar.failedToConnectTryAgain,
      );
      setIsConnecting(false);
    }
  }, [provider, onClose, t]);

  const handleContinueHere = useCallback(() => {
    if (authUrl) {
      window.location.href = authUrl;
    }
  }, [authUrl]);

  const handleClosePopupBlockedModal = useCallback(() => {
    setShowPopupBlockedModal(false);
    setIsConnecting(false);
    onClose();
  }, [onClose]);

  return (
    <>
      <AnimatePresence>
        {isOpen && !showPopupBlockedModal && (
          <ModalOverlay
            onClick={onClose}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2, ease: 'easeInOut'}}>
            <ModalWrapper
              onClick={e => e.stopPropagation()}
              initial={{y: 20, opacity: 0}}
              animate={{y: 0, opacity: 1}}
              exit={{y: -20, opacity: 0}}
              transition={{duration: 0.2, ease: 'easeInOut'}}
              whileHover={{
                scale: 1.01,
                transition: {duration: 0.2, ease: 'easeInOut'},
              }}>
              <WhiteContainer>
                <ModalInnerContent>
                  <HeaderContainer
                    initial={{opacity: 0, y: -10}}
                    animate={{opacity: 1, y: 0}}
                    transition={{
                      delay: 0.1,
                      duration: 0.2,
                      ease: 'easeInOut',
                    }}>
                    📅
                  </HeaderContainer>
                  <motion.div
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    transition={{
                      delay: 0.2,
                      duration: 0.2,
                      ease: 'easeInOut',
                    }}>
                    <TextHeading3
                      text={t.calendar.connectYourProvider.replace(
                        '{provider}',
                        providerName,
                      )}
                    />
                  </motion.div>
                  <MessageContainer
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    transition={{
                      delay: 0.3,
                      duration: 0.2,
                      ease: 'easeInOut',
                    }}>
                    <TextBodyMedium
                      text={t.calendar.eventsWillSyncWhenAttend}
                      style={{color: Palette.almostBlack500}}
                    />
                  </MessageContainer>

                  <BulletList>
                    <BulletItem>
                      <TextBodySmall
                        text={t.calendar.noManualDownloads}
                        color={Palette.almostBlack}
                      />
                    </BulletItem>
                    <BulletItem>
                      <TextBodySmall
                        text={t.calendar.eventsAppearInstantly}
                        color={Palette.almostBlack}
                      />
                    </BulletItem>
                  </BulletList>

                  {error && (
                    <ErrorMessageContainer
                      initial={{opacity: 0}}
                      animate={{opacity: 1}}
                      transition={{duration: 0.2}}>
                      <TextBodySmall text={error} color={Palette.systemError} />
                    </ErrorMessageContainer>
                  )}

                  <ButtonContainer>
                    <SecondaryButton
                      label={t.calendar.skipForNow}
                      onClick={onContinueWithoutConnection}
                      isDisabled={isConnecting}
                    />
                    <GoogleButton
                      label={
                        isConnecting
                          ? t.calendar.connecting
                          : t.calendar.connectProvider.replace(
                              '{provider}',
                              providerName,
                            )
                      }
                      onClick={handleConnectCalendar}
                      isDisabled={isConnecting}
                    />
                  </ButtonContainer>
                </ModalInnerContent>
              </WhiteContainer>
            </ModalWrapper>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <ConfirmationModal
        isOpen={showPopupBlockedModal}
        onClose={handleClosePopupBlockedModal}
        onConfirm={handleContinueHere}
        title={t.calendar.popupBlocked}
        message={t.calendar.popupBlockedDescription}
        confirmLabel={t.calendar.continueHere}
        cancelLabel={t.common.cancel}
        header={<div style={{fontSize: '48px'}}>🚫</div>}
      />

      <SuccessToastContainer isVisible={showSuccessToast}>
        <TextBodySmall
          text={t.calendar.calendarConnectedEventsSync}
          color={Palette.white}
        />
      </SuccessToastContainer>
    </>
  );
};
